/**
 * OrganizationReports — corporate-level talent performance report.
 *
 * Aggregates evaluation data across all departments and periods to show
 * organisation-wide competency performance. Accessible to roles with the
 * `corporate:tdp` permission. Supports quarterly filtering and table/chart
 * view toggles.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Table, BarChart3, TrendingUp, TrendingDown, Minus, Users, Target, Activity, AlertCircle } from 'lucide-react';
import { supabase } from '../../config/supabase';
import { useAuth } from '../../context/AuthContext';
import './OrganizationReports.css';

// Standard quarters — always shown regardless of DB data
const STANDARD_PERIODS = [
  'Q1: January – March',
  'Q2: April – June',
  'Q3: July – September',
  'Q4: October – December',
];

const REGIONS = [
  'All Regions',
  'Africa',
  'India',
  'China',
  'East Asia',
  'Middle East',
  'Europe',
  'North America (including the U.S.)',
];

const CorporatePerformanceReports = () => {
  const { organizationId } = useAuth();
  const [selectedPeriod, setSelectedPeriod] = useState(STANDARD_PERIODS[0]);
  const [selectedRegion, setSelectedRegion] = useState(REGIONS[0]);
  const [periods, setPeriods] = useState(STANDARD_PERIODS);
  const [departments, setDepartments] = useState([]);
  const [reportData, setReportData] = useState([]);
  const [viewMode, setViewMode] = useState('table');
  const [loading, setLoading] = useState(false);
  const [filtersLoading, setFiltersLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchReportData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data: evaluations, error: evalError } = await supabase
        .from('evaluations')
        .select('evaluation_id, department_id, evaluation_type')
        .eq('evaluation_period', selectedPeriod)
        .eq('status', 'Complete');

      if (evalError) throw evalError;

      if (!evaluations || evaluations.length === 0) {
        setReportData([]);
        setLoading(false);
        return;
      }

      const evalIds = evaluations.map(e => e.evaluation_id);

      const [ratingsRes, targetsRes] = await Promise.all([
        supabase
          .from('evaluation_ratings')
          .select('evaluation_id, rating')
          .in('evaluation_id', evalIds),
        supabase
          .from('performance_targets')
          .select('department_id, evaluation_type, target_score')
          .eq('evaluation_period', selectedPeriod),
      ]);

      if (ratingsRes.error) throw ratingsRes.error;

      const evalMap = {};
      evaluations.forEach(e => { evalMap[e.evaluation_id] = e; });

      const targetsMap = {};
      (targetsRes.data || []).forEach(t => {
        targetsMap[`${t.department_id}_${t.evaluation_type}`] = t.target_score;
      });

      const aggMap = {};
      (ratingsRes.data || []).forEach(r => {
        const ev = evalMap[r.evaluation_id];
        if (!ev || r.rating == null) return;
        const key = `${ev.department_id}_${ev.evaluation_type}`;
        if (!aggMap[key]) aggMap[key] = { sum: 0, count: 0 };
        aggMap[key].sum += r.rating;
        aggMap[key].count += 1;
      });

      const result = departments.map(dept => {
        const coreKey = `${dept.department_id}_Core Competency Evaluation`;
        const techKey = `${dept.department_id}_Technical Competency Evaluation`;

        const coreAgg = aggMap[coreKey];
        const techAgg = aggMap[techKey];

        const coreActual = coreAgg ? Math.round((coreAgg.sum / (coreAgg.count * 5)) * 100) : null;
        const techActual = techAgg ? Math.round((techAgg.sum / (techAgg.count * 5)) * 100) : null;

        const coreExpected = targetsMap[coreKey] != null ? Number(targetsMap[coreKey]) : 80;
        const techExpected = targetsMap[techKey] != null ? Number(targetsMap[techKey]) : 80;

        return {
          id: dept.department_id,
          name: dept.depart_name,
          coreCompetency: {
            expected: coreExpected,
            actual: coreActual,
            variance: coreActual !== null ? coreActual - coreExpected : null,
          },
          technicalCompetency: {
            expected: techExpected,
            actual: techActual,
            variance: techActual !== null ? techActual - techExpected : null,
          },
        };
      }).filter(d => d.coreCompetency.actual !== null || d.technicalCompetency.actual !== null);

      setReportData(result);
    } catch (err) {
      setError('Failed to load report data. Please try again.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [selectedPeriod, departments]);

  const fetchFilters = async () => {
    setFiltersLoading(true);
    try {
      const [periodsRes, deptsRes] = await Promise.all([
        supabase.from('evaluations').select('evaluation_period').order('evaluation_period'),
        supabase.from('department').select('department_id, depart_name').eq('organization_id', organizationId).order('depart_name'),
      ]);

      if (periodsRes.error) throw periodsRes.error;
      if (deptsRes.error) throw deptsRes.error;

      // Always include standard quarters; append any extra periods from DB
      const dbPeriods = (periodsRes.data || []).map(e => e.evaluation_period);
      const extras = dbPeriods.filter(p => !STANDARD_PERIODS.includes(p));
      const merged = [...STANDARD_PERIODS, ...new Set(extras)];

      setPeriods(merged);
      setDepartments(deptsRes.data || []);
    } catch (err) {
      setError('Failed to load filters. Please check your connection.');
      console.error(err);
    } finally {
      setFiltersLoading(false);
    }
  };

  useEffect(() => {
    if (organizationId) fetchFilters();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizationId]);

  useEffect(() => {
    if (selectedPeriod && departments.length > 0) {
      fetchReportData();
    }
  }, [selectedPeriod, departments, fetchReportData]);

  const depsWithCore = reportData.filter(d => d.coreCompetency.actual !== null);
  const depsWithTech = reportData.filter(d => d.technicalCompetency.actual !== null);

  const orgAverages = reportData.length > 0 ? {
    coreExpected: Math.round(reportData.reduce((s, d) => s + d.coreCompetency.expected, 0) / reportData.length),
    coreActual: depsWithCore.length > 0
      ? Math.round(depsWithCore.reduce((s, d) => s + d.coreCompetency.actual, 0) / depsWithCore.length)
      : null,
    techExpected: Math.round(reportData.reduce((s, d) => s + d.technicalCompetency.expected, 0) / reportData.length),
    techActual: depsWithTech.length > 0
      ? Math.round(depsWithTech.reduce((s, d) => s + d.technicalCompetency.actual, 0) / depsWithTech.length)
      : null,
  } : null;

  const overallVariance = orgAverages && orgAverages.coreActual != null && orgAverages.techActual != null
    ? Math.round(((orgAverages.coreActual + orgAverages.techActual) / 2) - ((orgAverages.coreExpected + orgAverages.techExpected) / 2))
    : null;

  const getVarianceIcon = (variance) => {
    if (variance === null) return <Minus size={13} />;
    if (variance > 0) return <TrendingUp size={13} />;
    if (variance < 0) return <TrendingDown size={13} />;
    return <Minus size={13} />;
  };

  const formatVariance = (v) => {
    if (v === null) return '—';
    return `${v > 0 ? '+' : ''}${v}%`;
  };

  return (
    <div className="corp-container">
      {/* Header */}
      <div className="corp-header">
        <h2 className="corp-title"><i className="bi bi-grid-3x3-gap" />Corporate Performance Report</h2>
        <p className="corp-subtitle">
          Comprehensive departmental performance analysis tracking core and technical competencies across organizational units
        </p>
        <hr className="corp-divider" />
      </div>

      {/* Filters */}
      <div className="corp-filters">
        <div className="corp-filter-group">
          <label className="corp-filter-label">Evaluation Period</label>
          {filtersLoading ? (
            <div className="corp-filter-skeleton" />
          ) : (
            <select
              className="corp-filter-select"
              value={selectedPeriod}
              onChange={(e) => setSelectedPeriod(e.target.value)}
              disabled={periods.length === 0}
            >
              {periods.length === 0
                ? <option value="">No periods available</option>
                : periods.map(p => <option key={p} value={p}>{p}</option>)
              }
            </select>
          )}
        </div>
        <div className="corp-filter-group">
          <label className="corp-filter-label">Region</label>
          <select
            className="corp-filter-select"
            value={selectedRegion}
            onChange={(e) => setSelectedRegion(e.target.value)}
          >
            {REGIONS.map(r => <option key={r} value={r}>{r}</option>)}
          </select>
        </div>
      </div>

      {/* Summary Cards */}
      {orgAverages && !loading && (
        <div className="corp-summary-grid">
          <div className="corp-summary-card">
            <div className="corp-summary-icon corp-icon-blue">
              <Users size={18} />
            </div>
            <div className="corp-summary-body">
              <span className="corp-summary-label">Departments Assessed</span>
              <span className="corp-summary-value">{reportData.length}</span>
            </div>
          </div>
          <div className="corp-summary-card">
            <div className="corp-summary-icon corp-icon-emerald">
              <Target size={18} />
            </div>
            <div className="corp-summary-body">
              <span className="corp-summary-label">Avg Core Score</span>
              <span className="corp-summary-value">
                {orgAverages.coreActual != null ? `${orgAverages.coreActual}%` : '—'}
              </span>
            </div>
          </div>
          <div className="corp-summary-card">
            <div className="corp-summary-icon corp-icon-violet">
              <Activity size={18} />
            </div>
            <div className="corp-summary-body">
              <span className="corp-summary-label">Avg Technical Score</span>
              <span className="corp-summary-value">
                {orgAverages.techActual != null ? `${orgAverages.techActual}%` : '—'}
              </span>
            </div>
          </div>
          <div className={`corp-summary-card ${overallVariance !== null ? (overallVariance >= 0 ? 'corp-card-pos' : 'corp-card-neg') : ''}`}>
            <div className={`corp-summary-icon ${overallVariance !== null ? (overallVariance >= 0 ? 'corp-icon-green' : 'corp-icon-red') : 'corp-icon-gray'}`}>
              {overallVariance !== null
                ? (overallVariance >= 0 ? <TrendingUp size={18} /> : <TrendingDown size={18} />)
                : <Minus size={18} />
              }
            </div>
            <div className="corp-summary-body">
              <span className="corp-summary-label">Overall Variance</span>
              <span className="corp-summary-value">
                {overallVariance !== null ? `${overallVariance >= 0 ? '+' : ''}${overallVariance}%` : '—'}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* View Toggle */}
      <div className="corp-view-toggle">
        <button
          className={`corp-toggle-btn ${viewMode === 'table' ? 'corp-toggle-active' : ''}`}
          onClick={() => setViewMode('table')}
        >
          <Table size={15} />
          <span>Table View</span>
        </button>
        <button
          className={`corp-toggle-btn ${viewMode === 'visual' ? 'corp-toggle-active' : ''}`}
          onClick={() => setViewMode('visual')}
        >
          <BarChart3 size={15} />
          <span>Visual Report</span>
        </button>
      </div>

      {/* Loading */}
      {loading && (
        <div className="corp-loading">
          <div className="corp-spinner" />
          <span>Loading report data...</span>
        </div>
      )}

      {/* Error */}
      {error && !loading && (
        <div className="corp-error">
          <AlertCircle size={18} />
          <span>{error}</span>
        </div>
      )}

      {/* Table View */}
      {!loading && !error && viewMode === 'table' && (
        <div className="corp-table-wrapper">
          {reportData.length > 0 ? (
            <table className="corp-table">
              <thead>
                <tr>
                  <th className="corp-th corp-th-dept" rowSpan="2">Department</th>
                  <th className="corp-th corp-th-core" colSpan="3">Core Competency</th>
                  <th className="corp-th corp-th-tech" colSpan="3">Technical Competency</th>
                </tr>
                <tr>
                  <th className="corp-th-sub">Expected</th>
                  <th className="corp-th-sub">Actual</th>
                  <th className="corp-th-sub">Variance</th>
                  <th className="corp-th-sub">Expected</th>
                  <th className="corp-th-sub">Actual</th>
                  <th className="corp-th-sub">Variance</th>
                </tr>
              </thead>
              <tbody>
                {reportData.map((dept, idx) => (
                  <tr key={dept.id} className={idx % 2 === 0 ? 'corp-row-even' : 'corp-row-odd'}>
                    <td className="corp-td corp-td-dept">{dept.name}</td>

                    {/* Core expected */}
                    <td className="corp-td corp-td-num">{dept.coreCompetency.expected}%</td>

                    {/* Core actual with mini bar */}
                    <td className="corp-td corp-td-actual">
                      {dept.coreCompetency.actual !== null ? (
                        <div className="corp-actual-cell">
                          <span className="corp-actual-val">{dept.coreCompetency.actual}%</span>
                          <div className="corp-mini-track">
                            <div
                              className="corp-mini-fill corp-mini-core"
                              style={{ width: `${dept.coreCompetency.actual}%` }}
                            />
                          </div>
                        </div>
                      ) : <span className="corp-null">—</span>}
                    </td>

                    {/* Core variance */}
                    <td className="corp-td">
                      {dept.coreCompetency.variance !== null ? (
                        <span className={`corp-variance-pill ${dept.coreCompetency.variance >= 0 ? 'corp-pill-pos' : 'corp-pill-neg'}`}>
                          {getVarianceIcon(dept.coreCompetency.variance)}
                          {formatVariance(dept.coreCompetency.variance)}
                        </span>
                      ) : <span className="corp-null">—</span>}
                    </td>

                    {/* Tech expected */}
                    <td className="corp-td corp-td-num">{dept.technicalCompetency.expected}%</td>

                    {/* Tech actual with mini bar */}
                    <td className="corp-td corp-td-actual">
                      {dept.technicalCompetency.actual !== null ? (
                        <div className="corp-actual-cell">
                          <span className="corp-actual-val">{dept.technicalCompetency.actual}%</span>
                          <div className="corp-mini-track">
                            <div
                              className="corp-mini-fill corp-mini-tech"
                              style={{ width: `${dept.technicalCompetency.actual}%` }}
                            />
                          </div>
                        </div>
                      ) : <span className="corp-null">—</span>}
                    </td>

                    {/* Tech variance */}
                    <td className="corp-td">
                      {dept.technicalCompetency.variance !== null ? (
                        <span className={`corp-variance-pill ${dept.technicalCompetency.variance >= 0 ? 'corp-pill-pos' : 'corp-pill-neg'}`}>
                          {getVarianceIcon(dept.technicalCompetency.variance)}
                          {formatVariance(dept.technicalCompetency.variance)}
                        </span>
                      ) : <span className="corp-null">—</span>}
                    </td>
                  </tr>
                ))}

                {/* Org average row */}
                {orgAverages && (
                  <tr className="corp-row-avg">
                    <td className="corp-td corp-td-dept">Organization Average</td>
                    <td className="corp-td corp-td-num">{orgAverages.coreExpected}%</td>
                    <td className="corp-td corp-td-num">
                      {orgAverages.coreActual != null ? `${orgAverages.coreActual}%` : '—'}
                    </td>
                    <td className="corp-td">
                      {orgAverages.coreActual != null ? (
                        <span className={`corp-variance-pill ${orgAverages.coreActual >= orgAverages.coreExpected ? 'corp-pill-pos' : 'corp-pill-neg'}`}>
                          {getVarianceIcon(orgAverages.coreActual - orgAverages.coreExpected)}
                          {formatVariance(orgAverages.coreActual - orgAverages.coreExpected)}
                        </span>
                      ) : '—'}
                    </td>
                    <td className="corp-td corp-td-num">{orgAverages.techExpected}%</td>
                    <td className="corp-td corp-td-num">
                      {orgAverages.techActual != null ? `${orgAverages.techActual}%` : '—'}
                    </td>
                    <td className="corp-td">
                      {orgAverages.techActual != null ? (
                        <span className={`corp-variance-pill ${orgAverages.techActual >= orgAverages.techExpected ? 'corp-pill-pos' : 'corp-pill-neg'}`}>
                          {getVarianceIcon(orgAverages.techActual - orgAverages.techExpected)}
                          {formatVariance(orgAverages.techActual - orgAverages.techExpected)}
                        </span>
                      ) : '—'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          ) : (
            <div className="corp-empty">
              <BarChart3 size={44} className="corp-empty-icon" />
              <p className="corp-empty-title">No data for this period</p>
              <p className="corp-empty-sub">No completed evaluations found for the selected evaluation period.</p>
            </div>
          )}
        </div>
      )}

      {/* Visual Report */}
      {!loading && !error && viewMode === 'visual' && (
        <div className="corp-visual-grid">
          {reportData.length > 0 ? (
            reportData.map(dept => (
              <div key={dept.id} className="corp-visual-card">
                <div className="corp-visual-card-head">
                  <h3 className="corp-visual-card-title">{dept.name}</h3>
                  <div className="corp-visual-badges">
                    {dept.coreCompetency.variance !== null && (
                      <span className={`corp-visual-badge ${dept.coreCompetency.variance >= 0 ? 'corp-vbadge-pos' : 'corp-vbadge-neg'}`}>
                        {getVarianceIcon(dept.coreCompetency.variance)}
                        Core {formatVariance(dept.coreCompetency.variance)}
                      </span>
                    )}
                    {dept.technicalCompetency.variance !== null && (
                      <span className={`corp-visual-badge ${dept.technicalCompetency.variance >= 0 ? 'corp-vbadge-pos' : 'corp-vbadge-neg'}`}>
                        {getVarianceIcon(dept.technicalCompetency.variance)}
                        Tech {formatVariance(dept.technicalCompetency.variance)}
                      </span>
                    )}
                  </div>
                </div>

                <div className="corp-visual-card-body">
                  {/* Core Competency Section */}
                  <div className="corp-visual-section">
                    <p className="corp-visual-section-title">Core Competency</p>
                    <div className="corp-bar-row">
                      <span className="corp-bar-label">Expected</span>
                      <div className="corp-bar-track">
                        <div
                          className="corp-bar-fill corp-bar-expected"
                          style={{ width: `${dept.coreCompetency.expected}%` }}
                        />
                      </div>
                      <span className="corp-bar-pct">{dept.coreCompetency.expected}%</span>
                    </div>
                    <div className="corp-bar-row">
                      <span className="corp-bar-label">Actual</span>
                      <div className="corp-bar-track">
                        {dept.coreCompetency.actual !== null ? (
                          <div
                            className="corp-bar-fill corp-bar-core"
                            style={{ width: `${dept.coreCompetency.actual}%` }}
                          />
                        ) : (
                          <span className="corp-bar-nodata">No data</span>
                        )}
                      </div>
                      <span className="corp-bar-pct">
                        {dept.coreCompetency.actual !== null ? `${dept.coreCompetency.actual}%` : '—'}
                      </span>
                    </div>
                  </div>

                  <div className="corp-visual-divider" />

                  {/* Technical Competency Section */}
                  <div className="corp-visual-section">
                    <p className="corp-visual-section-title">Technical Competency</p>
                    <div className="corp-bar-row">
                      <span className="corp-bar-label">Expected</span>
                      <div className="corp-bar-track">
                        <div
                          className="corp-bar-fill corp-bar-expected"
                          style={{ width: `${dept.technicalCompetency.expected}%` }}
                        />
                      </div>
                      <span className="corp-bar-pct">{dept.technicalCompetency.expected}%</span>
                    </div>
                    <div className="corp-bar-row">
                      <span className="corp-bar-label">Actual</span>
                      <div className="corp-bar-track">
                        {dept.technicalCompetency.actual !== null ? (
                          <div
                            className="corp-bar-fill corp-bar-tech"
                            style={{ width: `${dept.technicalCompetency.actual}%` }}
                          />
                        ) : (
                          <span className="corp-bar-nodata">No data</span>
                        )}
                      </div>
                      <span className="corp-bar-pct">
                        {dept.technicalCompetency.actual !== null ? `${dept.technicalCompetency.actual}%` : '—'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="corp-empty">
              <BarChart3 size={44} className="corp-empty-icon" />
              <p className="corp-empty-title">No data for this period</p>
              <p className="corp-empty-sub">No completed evaluations found for the selected evaluation period.</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CorporatePerformanceReports;
